import { useState } from "react";
import PropTypes from "prop-types";
import toast from "react-hot-toast";

import api from "../../utilities/api";
import DeleteConfirmModal from "./DeleteConfirmModal";

const plural = (count, word) => `${count} ${word}${count === 1 ? "" : "s"}`;

const errorMessage = (error) =>
  error.response?.data?.message || error.response?.data?.error || error.message || "Unknown error";

const toastError = (title, error) =>
  toast.error(`${title}\n${errorMessage(error)}`, {
    duration: 6000,
    position: "top-center",
    className: "whitespace-pre-line",
  });

/**
 * "Delete All References" button (admin): counts the standard references of all
 * records and drafts, asks for confirmation and empties them.
 *
 * @param {Object} props
 * @param {string} props.className - Button classes
 * @param {boolean} props.disabled - Disables the button (e.g. while the parent is busy)
 * @param {Function} props.onDeleted - Called after the references were deleted
 */
const DeleteAllRefsButton = ({ className, disabled = false, onDeleted = () => {} }) => {
  // { records, drafts } counts while the warning is open
  const [counts, setCounts] = useState(null);
  const [busy, setBusy] = useState(false);

  // Count first, so the warning shows what goes
  const handleClick = async () => {
    setBusy(true);
    try {
      const response = await api.get(`/import/standards-refs`);
      const { records, drafts } = response.data;
      if (!records.refs && !drafts.refs) {
        toast("No standard references to delete.", { position: "top-center" });
        return;
      }
      setCounts(response.data);
    } catch (error) {
      console.error("Error counting standard references:", error);
      toastError("Counting references failed!", error);
    } finally {
      setBusy(false);
    }
  };

  const handleDelete = async () => {
    setCounts(null);
    setBusy(true);
    try {
      const response = await api.delete(`/import/standards-refs`);
      toast.success("Standard references deleted!\n" + response.data.message, {
        duration: 5000,
        position: "top-center",
        className: "whitespace-pre-line",
      });
      onDeleted();
    } catch (error) {
      console.error("Error deleting standard references:", error);
      toastError("Delete failed!", error);
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <button
        type="button"
        onClick={handleClick}
        disabled={disabled || busy}
        className={className}
      >
        Delete All References
      </button>

      <DeleteConfirmModal
        isOpen={!!counts}
        onClose={() => setCounts(null)}
        onConfirm={handleDelete}
        title="Delete all standard references"
        message={
          counts
            ? `This deletes ALL standard references (every standard and framework): ${plural(counts.records.refs, "reference")} from ${plural(counts.records.documents, "record")} and ${plural(counts.drafts.refs, "reference")} from ${plural(counts.drafts.documents, "draft")}. This cannot be undone.`
            : ""
        }
        confirmLabel="Delete All"
      />
    </>
  );
};

DeleteAllRefsButton.propTypes = {
  className: PropTypes.string,
  disabled: PropTypes.bool,
  onDeleted: PropTypes.func,
};

export default DeleteAllRefsButton;
